<script>
  import { onMount } from "svelte";
  import { world } from "./../geo/world_topojson_new";
  //import { world } from "./../geo/wb_countries_topo";
  import { feature } from "topojson";
  import { geoPath } from "d3-geo";
  import { geoPatterson } from "d3-geo-projection";
  import { extent } from "d3-array";
  import { scaleSequential } from "d3-scale";
  import { select } from "d3-selection";
  import { zoom, zoomIdentity } from "d3-zoom";
  import {
    interpolateBlues,
    interpolateGreens,
    interpolateGreys,
    interpolateOranges,
    interpolatePurples,
    interpolateReds,
    interpolateOrRd,
    interpolateYlGn,
    interpolateYlOrRd,
    interpolateBuPu,
  } from "d3-scale-chromatic";
  import Legend from "./Legend.svelte";
  import Tooltip from "./Tooltip.svelte";
  import NoDataAvailable from "./NoDataAvailable.svelte";
  import { format } from "d3-format";
  import { displayData } from "./../stores";
  import {colors} from "./../colors"

  export let mapData;
  export let yVariable;
  export let chartTitle;
  export let primaryCountry;
  export let showOutlines;
  export let stroke = "#cccccc";
  export let paletteMin;
  export let paletteMax;
  export let reversePalette;
  export let legendLabel;
  export let paletteValue;

  const paletteMap = {
    interpolateBlues: interpolateBlues,
    interpolateGreens: interpolateGreens,
    interpolateGreys: interpolateGreys,
    interpolateOranges: interpolateOranges,
    interpolatePurples: interpolatePurples,
    interpolateReds: interpolateReds,
    interpolateOrRd: interpolateOrRd,
    interpolateYlGn: interpolateYlGn,
    interpolateYlOrRd: interpolateYlOrRd,
    interpolateBuPu: interpolateBuPu,
  };

  $: selectedPalette = paletteMap[paletteValue.value];

  const margin = { top: 0, bottom: 0, left: 0, right: 0 };
  const width = 900;
  const height = 600;

  let showTooltip = false;
  let tooltipContent;
  let tooltipX;
  let tooltipY;
  function handleMouseOver(event, ttContent) {
    showTooltip = true;
    tooltipX = event.clientX;
    tooltipY = event.clientY;
    tooltipContent = ttContent;
  }
  function handleMouseOut() {
    showTooltip = false;
  }

  // Remove Antarctica
  world.objects.WB_GAD_ADM0.geometries =
    world.objects.WB_GAD_ADM0.geometries.filter(
      (d) => d.properties.ISO_A3 !== ""
    );
  // Here non-member states are filtered out, but this also includes West Bank and Gaza and other things that should go on the map
  let countries = feature(world, world.objects.WB_GAD_ADM0).features.filter(d => d.properties.WB_STATUS === "Member State");

  const projection = geoPatterson().fitSize(
    [width, height],
    feature(world, world.objects.WB_GAD_ADM0)
  );
  const path = geoPath().projection(projection);

  let dataExtent = [];
  $: if (typeof mapData !== "undefined") {
    dataExtent = extent(mapData, (d) => d.y);
  }
  $: min = paletteMin ? paletteMin : dataExtent[0];
  $: max = paletteMax ? paletteMax : dataExtent[1];
  $: domain = reversePalette ? [max, min] : [min, max];
  $: colorScale = scaleSequential(selectedPalette)
    .domain(domain)
    .unknown("#ccc");

  let zooming = zoom().scaleExtent([1, 8]).on("zoom", handleZoom);
  function handleZoom(e) {
      select("svg g#map").attr("transform", e.transform);
    };
  onMount(() => {
    select("svg g#map").call(zooming);
    if (typeof primaryCountry !== "undefined" && primaryCountry !== null) {
    primaryZoom(primaryCountry.wbcode)
  }
  });

  let primaryCountryExtent;
  $: if (typeof primaryCountry !== "undefined" && primaryCountry !== null) {
    primaryZoom(primaryCountry.wbcode)
  }

  function primaryZoom(primarycountryID) {
    primaryCountryExtent = countries.find(
      (d) => d.properties.ISO_A3 === primarycountryID
    );
    const [[x0, y0], [x1, y1]] = path.bounds(primaryCountryExtent);
    select("svg g#map").call(
      zooming.transform,
      zoomIdentity
        .translate(width / 2, height / 2)
        .scale(
          Math.min(8, 0.9 / Math.max((x1 - x0) / width, (y1 - y0) / height))
        )
        .translate(-(x0 + x1) / 2, -(y0 + y1) / 2)
    );
  }
</script>

<svg {width} {height} class="chart">
  <g transform={`translate(${margin.left},${margin.top})`} id="map">
    <rect
      x={0}
      y={0}
      width={width - margin.left - margin.right}
      height={height - margin.top - margin.right}
      fill={"#ffffff"}
    />
    {#if typeof mapData !== "undefined" && mapData.length === 0}
      <NoDataAvailable {width} {height} />
    {/if}
    <text x={0} y={20} font-size={24}>{chartTitle}</text>

    {#if typeof mapData !== "undefined" && mapData.length > 0}
      {#each countries as country}
        <path
          class="feature"
          id={"path-" + country.properties.ISO_A3}
          d={path(country.geometry)}
          stroke={showOutlines ? stroke : "none"}
          fill={$displayData
            ? mapData.find((d) => d.wbcode === country.properties.ISO_A3)
              ? colorScale(
                  mapData.find((d) => d.wbcode === country.properties.ISO_A3).y
                )
              : "#dddddd"
            : "#dddddd"}
          on:mouseover={(event) =>
            handleMouseOver(
              event,
              `<p class="countryname">${
                mapData.find((d) => d.wbcode === country.properties.ISO_A3)
                  ? mapData.find((d) => d.wbcode === country.properties.ISO_A3)
                      .label
                  : "No data"
              }</p>
            <p>${yVariable.label}: <span class="value">${
                mapData.find((d) => d.wbcode === country.properties.ISO_A3)
                  ? format(",.1f")(
                      mapData.find(
                        (d) => d.wbcode === country.properties.ISO_A3
                      ).y
                    )
                  : "No data"
              }</span>
              </p>
              <p>Year: ${
                mapData.find((d) => d.wbcode === country.properties.ISO_A3)
                  ? mapData.find((d) => d.wbcode === country.properties.ISO_A3)
                      .year
                  : "No data"
              }</p>`
            )}
          on:focus={handleMouseOver}
          on:mouseout={handleMouseOut}
          on:blur={handleMouseOut}
        />
      {/each}
    {/if}
    {#if typeof primaryCountry !== "undefined"  && typeof primaryCountryExtent !== "undefined"}
              <path
                class="feature"
              d={path(primaryCountryExtent.geometry)}
              fill="none"
              stroke-width={1.5}
              stroke={colors.primary}
              ></path>
    {/if}
  </g>
  <g transform={`translate(20,${height - 64})`} class="legend-container">
    <Legend {colorScale} {reversePalette} {legendLabel} {selectedPalette} />
  </g>
</svg>
{#if showTooltip}
  <Tooltip {tooltipX} {tooltipY} {tooltipContent} />
{/if}

<style>
  .feature {
    vector-effect: non-scaling-stroke;
  }
</style>
